import { createApi } from '@reduxjs/toolkit/query/react';
import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';

import { URL } from 'api/constants';
import { apiQuery } from 'store/query';
import { generateUrl } from 'utils/helpers';
import { isLogoutAction } from 'store/slices/auth';
import { providesList, cacheByIdArg, invalidatesList } from 'utils/query-cache';

import {
  PetBreed,
  BreedsListResponse,
  CreateBreedPayload,
  UpdateBreedPayload,
  BreedMutationResponse,
} from './types';

const breedsAdapter = createEntityAdapter<PetBreed, number>({
  selectId: (item) => item.id,
});

const tagType = 'Breeds';

export const breedsApi = createApi({
  tagTypes: [tagType],
  baseQuery: apiQuery,
  reducerPath: 'breedsApi',
  endpoints: (build) => ({
    delete: build.mutation<PetBreed, number>({
      invalidatesTags: invalidatesList(tagType),
      query: (id) => ({
        method: 'delete',
        url: generateUrl(URL.DELETE_MASTER_BREED, { id }),
      }),
    }),

    list: build.query<PetBreed[], void>({
      providesTags: (result) => providesList(tagType)(result),
      transformResponse: (response: BreedsListResponse) => response.data,
      query: () => ({
        method: 'get',
        url: URL.GET_BREEDS_MASTER_LIST,
      }),
    }),

    create: build.mutation<PetBreed, CreateBreedPayload>({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: BreedMutationResponse) => response.data,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.ADD_MASTER_BREED,
      }),
    }),

    update: build.mutation<PetBreed, UpdateBreedPayload>({
      transformResponse: (response: BreedMutationResponse) => response.data,
      invalidatesTags: (result, error, { breed_id }) =>
        cacheByIdArg(tagType)(result, error, breed_id),
      query: ({ name, breed_id, species_id }) => ({
        method: 'patch',
        data: {
          name,
          species_id,
        },
        url: generateUrl(URL.UPDATE_MASTER_BREED, { id: breed_id }),
      }),
    }),
  }),
});

export const breedsSlice = createSlice({
  reducers: {},
  name: 'breeds',
  initialState: breedsAdapter.getInitialState(),
  extraReducers: (builder) => {
    builder.addMatcher(
      breedsApi.endpoints.list.matchFulfilled,
      (state, { payload }) => {
        breedsAdapter.setAll(state, payload);
      }
    );

    builder.addMatcher(
      breedsApi.endpoints.create.matchFulfilled,
      (state, { payload }) => {
        breedsAdapter.addOne(state, payload);
      }
    );

    builder.addMatcher(
      breedsApi.endpoints.update.matchFulfilled,
      (state, { payload }) => {
        breedsAdapter.updateOne(state, {
          changes: payload,
          id: breedsAdapter.selectId(payload),
        });
      }
    );

    builder.addMatcher(isLogoutAction, (state) => {
      breedsAdapter.removeAll(state);
    });
  },
});

export const {
  useListQuery,
  useCreateMutation,
  useUpdateMutation,
  useDeleteMutation,
} = breedsApi;
